import { AnalyticsEventName } from "@albertine/analytics-client";
import { createContext, type ReactNode, use, useEffect } from "react";
import { parentOriginFromLocation } from "@/lib/parentFrame";
import type { ChatThreadActions } from "../-context/chatThreadActions";
import { type ChatScope, getChatScopeKey } from "../-data/chatApi";
import {
    type ChatOutboxDraft,
    reconcileChatOutboxWithSentMessage,
    useChatOutbox,
} from "../-data/chatOutbox";
import { getMockRequest, toDisplayContext } from "../-data/mockRequest";
import { useChatThread } from "../-data/useChatThread";
import { getErrorMessage } from "../-utils/chatErrors";
import { type ChatSearch, resolveChatRequestId } from "../-utils/chatSearchSchema";
import { composerLayoutHeight } from "../-utils/composerLayoutHeight";
import type { ConciergeLinkContext } from "../-utils/conciergeLinks";
import { trackChatAnalytics } from "../-utils/trackChatAnalytics";
import { useChatLiveEvents } from "./useChatLiveEvents";
import { useChatMutations } from "./useChatMutations";
import { useChatThreadView } from "./useChatThreadView";
import { useHostComposer } from "./useHostComposer";
import { useMarkThreadRead } from "./useMarkThreadRead";
import { useMessageHighlight } from "./useMessageHighlight";
import { holdNativeKeyboard, useNativeComposerInset } from "./useNativeComposerInset";
import { useNotifyHostComposerDismiss } from "./useNotifyHostComposerDismiss";
import { useRequestStatusBadge } from "./useRequestStatusBadge";

type ChatPageValue = ReturnType<typeof useChatPageState>;

const ChatPageContext = createContext<ChatPageValue | null>(null);

export function ChatPageProvider({
    search,
    children,
}: {
    search: ChatSearch;
    children: ReactNode;
}) {
    const value = useChatPageState(search);

    return <ChatPageContext value={value}>{children}</ChatPageContext>;
}

export function useChatPage(): ChatPageValue {
    const value = use(ChatPageContext);

    if (!value) {
        throw new Error("useChatPage must be used within ChatPageProvider");
    }

    return value;
}

function useChatPageState(search: ChatSearch) {
    const { locale } = search;
    const requestId = resolveChatRequestId(search);

    const scope: ChatScope = requestId
        ? { type: "request", requestId }
        : { type: "general" };
    const scopeKey = getChatScopeKey(scope);

    const {
        messages,
        conversationId,
        unreadMessageIds,
        canFetchPrevious,
        isFetchingPrevious,
        fetchPreviousMessages,
        fetchPreviousUntilMessageLoaded,
        resetPreviousFetchFailure,
        queryKey,
        isFetching,
        isFetchedAfterMount,
        isPending,
        isError,
        error,
    } = useChatThread(scope);
    const outboxEntries = useChatOutbox(scopeKey);

    useEffect(
        function reconcileConfirmedMessagesWithOutbox() {
            for (const message of messages) {
                reconcileChatOutboxWithSentMessage(message);
            }
        },
        [messages],
    );

    const { highlightedMessageId, highlightMessage } = useMessageHighlight();

    const {
        sendError,
        sendScrollGeneration,
        updateThread,
        sendDraft,
        retrySend,
        startReply,
        react,
        removeReaction,
    } = useChatMutations({ queryKey, scope });

    const { markThreadRead, markRetryToken } = useMarkThreadRead({
        conversationId,
        queryKey,
        requestId,
    });

    const display = requestId ? toDisplayContext(getMockRequest(requestId)) : undefined;
    const linkContext: ConciergeLinkContext = {
        parentOrigin: parentOriginFromLocation(),
        itemLinkPrefix: search.itemLinkPrefix ?? null,
        requestId,
    };

    const { dayGroups, isWaitingForAgentReply, imageUrls } = useChatThreadView({
        messages,
        outboxEntries,
        display,
        linkContext,
    });

    const { chatError } = useChatLiveEvents({
        scope,
        isPending,
        updateThread,
    });

    const nativeComposer = useNativeComposerInset();
    const requestStatusBadge = useRequestStatusBadge(
        isWaitingForAgentReply,
        window.isIosNativeWebView ? nativeComposer.keyboardOpen : undefined,
    );

    function sendDraftWithRequestStatus(draft: ChatOutboxDraft) {
        requestStatusBadge.onMessageSent();
        sendDraft(draft);
    }

    const { composerHeight: hostComposerHeight } = useHostComposer({
        scopeKey,
        sendDraft: sendDraftWithRequestStatus,
        onComposerActiveChange: (isActive) => {
            if (window.isIosNativeWebView) {
                holdNativeKeyboard(isActive);
                return;
            }
            requestStatusBadge.onComposerActiveChange(isActive);
        },
    });
    const composerHeight = composerLayoutHeight({
        nativeInset: nativeComposer.inset,
        hostComposerHeight,
    });
    useNotifyHostComposerDismiss();

    const errorMessage =
        sendError ?? chatError?.message ?? (isError ? getErrorMessage(error) : null);

    const threadActions: ChatThreadActions = {
        highlightedMessageId,
        onHighlightMessage: highlightMessage,
        onReply: startReply,
        onReact: react,
        onRemoveReaction: removeReaction,
        onCopy: (messageId) => {
            trackChatAnalytics(AnalyticsEventName.CHAT_MESSAGE_COPIED, {
                message_id: messageId,
            });
        },
        onRetrySend: (entry) => {
            requestStatusBadge.onMessageSent();
            retrySend(entry);
        },
    };

    return {
        locale,
        requestId,
        conversationId,
        unreadMessageIds,
        scopeKey,
        isFetching,
        isFetchedAfterMount,
        isPending,
        canFetchPrevious,
        isFetchingPrevious,
        fetchPreviousMessages,
        fetchPreviousUntilMessageLoaded,
        resetPreviousFetchFailure,
        markRetryToken,
        markThreadRead,
        dayGroups,
        imageUrls,
        requestStatusBadge,
        messageIds: messages.map((message) => message.id),
        agentMessageIds: new Set(
            messages
                .filter((message) => message.sender.type === "agent")
                .map((message) => message.id),
        ),
        errorMessage,
        composerHeight,
        sendScrollGeneration,
        sendDraft: sendDraftWithRequestStatus,
        threadActions,
    };
}
